import React, { useState } from 'react'
import styled from '@emotion/styled'
import { useDispatch } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import { Button } from '@mobi/component-library/Base/Buttons'
import { Password } from '@mobi/component-library/Selection&Input/Inputs'
import { spacing } from '@mobi/component-library/Foundations'
import { mfaCodeText, VerificationStandardError } from '../Helpers/helpers'
import { authenticateWithMfa } from '../Services/mfaService'
import { useBiometricsCredentials } from '@core/Areas/Login/helpers/useBiometricsCredentials'
import { VerificationCodeForm } from './VerificationCodeForm'
import { MfaCodePageText } from '../Pages/VerificationCodePage.styles'
import { useAppSelector } from '@core/Store/hooks'
import { getTrustedDeviceDetails } from '@core/Areas/Login/Store/selectors'
import { setTrustedDeviceDetails } from '@core/Areas/Login/Store'

interface PasswordVerificationProps {
  onVerify: VoidFunction
  onCancel: VoidFunction
  accountNumber: number
}

type MfaChallengeInformation = {
  mfaSessionId: string
  mfaFactor: 'SMS' | 'EMAIL'
  mfaHint: string
}

export const PasswordVerification: React.FC<PasswordVerificationProps> = ({
  onVerify,
  onCancel,
  accountNumber,
}) => {
  const dispatch = useDispatch()
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [challenge, setChallenge] = useState<MfaChallengeInformation | null>(null)
  const trustedDevices = useAppSelector(getTrustedDeviceDetails)

  useBiometricsCredentials((_, savedPassword) => {
    if (password === '') {
      setPassword(savedPassword)
      handleVerify(savedPassword)
    }
  })

  const handleVerify = async (passwordOverride?: string, mfaCode?: string) => {
    const resolvedPassword = passwordOverride ?? password

    if (!resolvedPassword) {
      setError('Please enter your password')
      return
    }
    setIsLoading(true)

    const storedDeviceDetailsForAccount = trustedDevices[accountNumber]

    const newDeviceSecret = uuidv4()

    const payload =
      !challenge || !mfaCode
        ? {
            accountNumber,
            password: resolvedPassword,
            deviceSecret: storedDeviceDetailsForAccount?.deviceSecret ?? newDeviceSecret,
            deviceGroupKey: storedDeviceDetailsForAccount?.deviceGroupKey,
            deviceKey: storedDeviceDetailsForAccount?.deviceKey,
          }
        : {
            accountNumber,
            mfaSessionId: challenge.mfaSessionId,
            mfaFactor: challenge.mfaFactor,
            mfaCode,
          }

    try {
      const result = await authenticateWithMfa(payload)

      if (result.status === 'SUCCESSFUL') {
        if (result.newDeviceKey && result.newDeviceGroupKey) {
          dispatch(
            setTrustedDeviceDetails({
              accountIdentifier: accountNumber.toString(),
              deviceDetails: {
                deviceGroupKey: result.newDeviceGroupKey,
                deviceKey: result.newDeviceKey,
                deviceSecret: newDeviceSecret,
                isRemembered: false,
              },
            })
          )
        }
        onVerify()
      } else if (result.status === 'MFA_REQUIRED') {
        const challengeData = {
          mfaSessionId: result.mfaSessionId,
          mfaFactor: result.mfaFactor,
          mfaHint: result.mfaHint,
        }
        setChallenge(challengeData)
      } else {
        if ('error' in result) {
          throw new Error(result.error ?? 'Verification failed')
        }
        throw new Error('Authentication unsuccessful')
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Verification failed'
      if (challenge == null) {
        setError(errorMessage)
      } else {
        throw new VerificationStandardError(errorMessage)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = async () => {
    await handleVerify()
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleVerify()
    }
  }

  return (
    <ContainerStyled>
      {challenge == null ? (
        <>
          <p>
            Please enter your password to manage your TABtouch account multi-factor authentication
            (MFA) settings.
          </p>
          <form onKeyDown={handleKeyDown}>
            <div style={{ margin: `0 ${spacing.md}` }}>
              <Password
                id='password'
                name='password'
                label='Password'
                value={password}
                onChange={e => setPassword(e.target.value)}
                isInvalid={!!error}
                helpText={error || undefined}
                onFocus={() => setError('')}
              />
            </div>
            <ButtonContainerStyled>
              <Button
                onClick={() => handleVerify()}
                color='primary'
                disabled={!password}
                isLoading={isLoading}
              >
                Continue
              </Button>
              <Button onClick={onCancel} color='tertiary_grey'>
                Cancel
              </Button>
            </ButtonContainerStyled>
          </form>
        </>
      ) : (
        <VerificationCodeForm
          onVerify={code => handleVerify(undefined, code)}
          onResend={handleResend}
          shouldShowHeader={false}
          analyticsFlowName='Login'
          analyticsMethodName={challenge.mfaFactor}
        >
          <MfaCodePageText>{mfaCodeText(challenge.mfaFactor, challenge.mfaHint)}</MfaCodePageText>
        </VerificationCodeForm>
      )}
    </ContainerStyled>
  )
}

// ======
// Styles
// ======

const ContainerStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.md,
  maxWidth: '80em',
  margin: '0 auto',
  marginTop: '-1rem',
  padding: `${spacing.sm} ${spacing.smx1}`,
  p: {
    textAlign: 'left',
  },
})

const ButtonContainerStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.md,
  margin: spacing.md,
})
